import {
  type MaybeRefOrGetter,
  type Ref,
  onBeforeUnmount,
  onMounted,
  ref,
  toValue,
  watch,
} from 'vue'

import { isBrowser } from '@backmarket/utils/env/isBrowser'

import type {
  ArcFit,
  ArcTextOptions,
  GlyphLayout,
  MeasureContext,
} from '../../Sticker.types'

import {
  BOTTOM_CENTER_DEG,
  BOTTOM_RADIUS,
  BOTTOM_SPAN_DEG,
  CENTER,
  DEG,
  MAX_BINARY_SEARCH_ITERATIONS,
  TOP_CENTER_DEG,
  TOP_RADIUS,
  TOP_SPAN_DEG,
  VIEWBOX,
  fontConfig,
} from './useArcText.constants'

// ── Glyph cache ──────────────────────────────────────────────────────────

/** Cache returned by `createGlyphCache`. */
interface GlyphCache {
  setFont(font: string): void
  width(text: string): number
  widthsOf(graphemes: string[]): number[]
  clear(): void
}

/**
 * Memoizes `CanvasRenderingContext2D.measureText` results so the binary
 * search in `fitTextToArc` doesn't re-measure the same string at the same
 * font size on every iteration. One cache is created per Sticker instance
 * (inside `render()`) and cleared on DPR changes or font-load completion.
 */
export function createGlyphCache(ctx: MeasureContext): GlyphCache {
  const widths = new Map<string, number>()

  function width(text: string): number {
    const key = `${ctx.font}\u0000${text}`
    const hit = widths.get(key)
    if (hit !== undefined) return hit
    const w = ctx.measureText(text).width
    widths.set(key, w)

    return w
  }

  return {
    setFont(font: string) {
      // eslint-disable-next-line no-param-reassign
      ctx.font = font
    },
    width,
    widthsOf(graphemes: string[]): number[] {
      return graphemes.map((g) => width(g))
    },
    clear() {
      widths.clear()
    },
  }
}

// ── Pure layout / fitting functions ──────────────────────────────────────

/**
 * Segment a string into graphemes using `Intl.Segmenter` (never
 * `String.prototype.split('')` — it breaks on accented letters, combining
 * marks, emoji, and surrogate pairs). Falls back to `Array.from` when
 * `Intl.Segmenter` is unavailable.
 */
export function segmentGraphemes(text: string, locale: string): string[] {
  if (typeof Intl.Segmenter !== 'undefined') {
    const segmenter = new Intl.Segmenter(locale, { granularity: 'grapheme' })

    return Array.from(segmenter.segment(text), (s) => s.segment)
  }

  return Array.from(text)
}

function fontShorthand(weight: number, sizePx: number, family: string): string {
  return `${weight} ${sizePx}px ${family}`
}

/** Arc length available for text: `radius * |endAngle - startAngle|`. */
export function availableArcLength(options: ArcTextOptions): number {
  return options.radius * Math.abs(options.endAngle - options.startAngle)
}

/** Total horizontal space occupied by the given widths plus inter-glyph tracking. */
export function computeTotalWidth(widths: number[], tracking: number): number {
  if (widths.length === 0) return 0
  const sum = widths.reduce((acc, w) => acc + w, 0)

  return sum + tracking * Math.max(widths.length - 1, 0)
}

/**
 * Pure geometry step: given a font size and tracking (already decided by
 * `fitTextToArc`), compute the (x, y, rotation) of each glyph so the text
 * sits centered on the arc midpoint. Each glyph is rotated so its baseline is
 * tangent to the circle; `inside` flips the tangent to face the center.
 */
export function placeGlyphsAlongArc(
  options: ArcTextOptions,
  graphemes: string[],
  widths: number[],
  fontSize: number,
  tracking: number,
): GlyphLayout[] {
  const { center, radius, startAngle, endAngle, inside } = options
  const direction = Math.sign(endAngle - startAngle)
  const midAngle = (startAngle + endAngle) / 2
  const totalWidth = computeTotalWidth(widths, tracking)

  const positions: number[] = []
  let cumulative = 0
  for (let i = 0; i < widths.length; i += 1) {
    positions.push(cumulative + widths[i] / 2)
    cumulative += widths[i] + tracking
  }

  const tangent = inside ? -Math.PI / 2 : Math.PI / 2

  return widths.map((w, i) => {
    const delta = (positions[i] - totalWidth / 2) / radius
    const angle = midAngle + direction * delta
    const x = center.x + Math.cos(angle) * radius
    const y = center.y + Math.sin(angle) * radius

    return { grapheme: graphemes[i], x, y, rotation: angle + tangent, width: w }
  })
}

/**
 * Decide the font size and tracking that fit the text within the arc, then
 * delegate per-glyph positioning to `placeGlyphsAlongArc`. Binary-searches
 * `[minFontSize, maxFontSize]` when the text overflows at `maxFontSize`;
 * tracking is grown up to `maxTracking` to fill the arc. Never stretches
 * glyphs; never exceeds `availableArcLength`.
 */
export function fitTextToArc(
  cache: GlyphCache,
  options: ArcTextOptions,
  graphemes: string[],
): ArcFit {
  const available = availableArcLength(options)
  const gaps = Math.max(graphemes.length - 1, 0)

  // 1. Text fits at max font size — apply fill tracking, done.
  cache.setFont(
    fontShorthand(options.fontWeight, options.maxFontSize, options.fontFamily),
  )
  if (cache.width(options.text) <= available) {
    const widths = cache.widthsOf(graphemes)
    const fillTracking = computeFillTracking(
      widths,
      gaps,
      available,
      options.maxTracking,
    )
    const glyphs = placeGlyphsAlongArc(
      options,
      graphemes,
      widths,
      options.maxFontSize,
      fillTracking,
    )

    return { fontSize: options.maxFontSize, tracking: fillTracking, glyphs }
  }

  // 2. Still overflows at min font size — clamp and accept the overflow.
  cache.setFont(
    fontShorthand(options.fontWeight, options.minFontSize, options.fontFamily),
  )
  if (cache.width(options.text) > available) {
    const widths = cache.widthsOf(graphemes)
    const glyphs = placeGlyphsAlongArc(
      options,
      graphemes,
      widths,
      options.minFontSize,
      0,
    )

    return { fontSize: options.minFontSize, tracking: 0, glyphs }
  }

  // 3. min fits, max overflows — binary-search the largest font that fits.
  // Iteration cap is a safety bound only; the real termination is
  // `high - low > 0.01`, which converges in ~9 iterations for a [11, 14] range.
  let low = options.minFontSize
  let high = options.maxFontSize
  let bestSize = options.minFontSize

  for (
    let iter = 0;
    iter < MAX_BINARY_SEARCH_ITERATIONS && high - low > 0.01;
    iter += 1
  ) {
    const mid = (low + high) / 2
    cache.setFont(fontShorthand(options.fontWeight, mid, options.fontFamily))
    if (cache.width(options.text) <= available) {
      bestSize = mid
      low = mid
    } else {
      high = mid
    }
  }

  cache.setFont(fontShorthand(options.fontWeight, bestSize, options.fontFamily))
  const widths = cache.widthsOf(graphemes)
  const fillTracking = computeFillTracking(
    widths,
    gaps,
    available,
    options.maxTracking,
  )
  const glyphs = placeGlyphsAlongArc(
    options,
    graphemes,
    widths,
    bestSize,
    fillTracking,
  )

  return { fontSize: bestSize, tracking: fillTracking, glyphs }
}

/**
 * Tracking (inter-glyph spacing) that spreads the text evenly across the
 * arc's available length without exceeding `maxTracking`. Returns 0 when
 * there are no gaps or the text already fills (or overflows) the arc.
 */
function computeFillTracking(
  widths: number[],
  gaps: number,
  available: number,
  maxTracking: number,
): number {
  if (gaps === 0) return 0
  const naturalSum = computeTotalWidth(widths, 0)

  return Math.min(maxTracking, Math.max(0, (available - naturalSum) / gaps))
}

/**
 * Paint a fitted arc onto a canvas 2D context, grapheme by grapheme. Each
 * glyph is centered on its arc point using the advance width (not the ink
 * left-bearing) so inter-glyph spacing stays uniform.
 */
export function paintArcTextOnCanvas(
  ctx: CanvasRenderingContext2D,
  options: ArcTextOptions,
  fit: ArcFit,
) {
  if (fit.glyphs.length === 0) return
  ctx.save()
  // eslint-disable-next-line no-param-reassign
  ctx.font = fontShorthand(options.fontWeight, fit.fontSize, options.fontFamily)
  // eslint-disable-next-line no-param-reassign
  ctx.fillStyle = options.fillStyle
  // eslint-disable-next-line no-param-reassign
  ctx.textBaseline = 'alphabetic'

  for (const glyph of fit.glyphs) {
    ctx.save()
    ctx.translate(glyph.x, glyph.y)
    ctx.rotate(glyph.rotation)
    ctx.fillText(glyph.grapheme, -glyph.width / 2, 0)
    ctx.restore()
  }
  ctx.restore()
}

// ── Arc configuration ────────────────────────────────────────────────────

/**
 * Builds the two arc text option sets from the current localized strings.
 * The top arc reads left-to-right across the top (outside the circle); the
 * bottom arc reads left-to-right across the bottom (inside the circle).
 */
export function buildArcTextOptions(
  topText: string,
  bottomText: string,
): ArcTextOptions[] {
  const top: ArcTextOptions = {
    text: topText,
    center: CENTER,
    radius: TOP_RADIUS,
    startAngle: (TOP_CENTER_DEG - TOP_SPAN_DEG / 2) * DEG,
    endAngle: (TOP_CENTER_DEG + TOP_SPAN_DEG / 2) * DEG,
    inside: false,
    ...fontConfig,
  }

  // Decreasing angle (west → south → east) so bottom text reads left-to-right.
  const bottom: ArcTextOptions = {
    text: bottomText,
    center: CENTER,
    radius: BOTTOM_RADIUS,
    startAngle: (BOTTOM_CENTER_DEG + BOTTOM_SPAN_DEG / 2) * DEG,
    endAngle: (BOTTOM_CENTER_DEG - BOTTOM_SPAN_DEG / 2) * DEG,
    inside: true,
    ...fontConfig,
  }

  return [top, bottom]
}

// ── Composable ───────────────────────────────────────────────────────────

/**
 * Renders one or more arcs of text onto a transparent canvas overlay. The
 * canvas uses a fixed `VIEWBOX × VIEWBOX` user space (matching the SVG `0 0
 * 128 128` viewBox) scaled by the device pixel ratio. CSS sizes the canvas to
 * fill its parent, so CSS resizes do NOT require a redraw — only DPR changes
 * do (observed via the `resize` event). The first paint is scheduled on the
 * next animation frame so it does not run synchronously during hydration.
 */
export function useArcText(
  canvas: Readonly<Ref<HTMLCanvasElement | null>>,
  options: {
    arcs: MaybeRefOrGetter<ArcTextOptions[]>
    locale: MaybeRefOrGetter<string>
  },
): { isCanvasReady: Ref<boolean> } {
  const isCanvasReady = ref(false)

  let raf = 0
  let dpr = 0
  let cache: GlyphCache | undefined

  function render() {
    const el = canvas.value
    if (!el) return
    const parent = el.parentElement
    if (!parent) return
    const ctx = el.getContext('2d')
    if (!ctx) return

    const newDpr = window.devicePixelRatio || 1
    if (newDpr !== dpr) {
      dpr = newDpr
      // Setting width/height resets the context transform and contents.
      el.width = Math.round(VIEWBOX * dpr)
      el.height = Math.round(VIEWBOX * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      cache = undefined
    }

    ctx.clearRect(0, 0, VIEWBOX, VIEWBOX)

    if (!cache) cache = createGlyphCache(ctx as unknown as MeasureContext)

    const color = window.getComputedStyle(parent).color || 'currentColor'
    const arcs = toValue(options.arcs)
    const locale = toValue(options.locale)

    for (const arc of arcs) {
      const filled = { ...arc, fillStyle: arc.fillStyle || color }
      const graphemes = segmentGraphemes(filled.text, locale)
      const fit = fitTextToArc(cache, filled, graphemes)
      paintArcTextOnCanvas(ctx, filled, fit)
    }

    isCanvasReady.value = true
  }

  function schedule() {
    if (!isBrowser()) return
    cancelAnimationFrame(raf)
    raf = requestAnimationFrame(render)
  }

  onMounted(() => {
    window.addEventListener('resize', schedule, { passive: true })

    if (document.fonts?.ready) {
      document.fonts.ready
        .then(() => {
          cache = undefined
          schedule()
        })
        .catch(() => undefined)
    }

    schedule()
  })

  onBeforeUnmount(() => {
    cancelAnimationFrame(raf)
    window.removeEventListener('resize', schedule)
  })

  watch(
    () => toValue(options.arcs),
    () => schedule(),
  )

  return { isCanvasReady }
}
